import {
  Badge,
  Button,
  Drawer,
  Group,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { IconMicroscope, IconPlus } from '@tabler/icons-react';
import { Patient, ServiceRequest, Specimen } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { SpecimenDisplayCard } from '../specimens/SpecimenDisplayCard';
import { useDisclosure } from '@mantine/hooks';
import { OrderFormData } from './OrderForm';
import { SpecimenSelectionContent } from './SpecimenSelectionContent';

interface SpecimenBoxProps {
  specimens: Specimen[];
  patient: Patient | undefined;
  serviceRequest: ServiceRequest;
  orderFormData: OrderFormData;
  onAdd: () => Promise<void>;
  onViewSpecimen?: (specimen: Specimen) => void;
  showAdd?: boolean;
  title?: string;
}

export function SpecimenBox({
  specimens,
  patient,
  serviceRequest,
  orderFormData,
  onAdd,
  onViewSpecimen,
  showAdd = true,
  title,
}: SpecimenBoxProps) {
  const { t } = useTranslation(['lims', 'common']);
  const [showSpecimenModal, { open, close }] = useDisclosure(false);

  return (
    <>
      <Paper shadow="sm" p="md" withBorder flex="1" pos="relative">
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap="xs">
              <Text size="sm" fw={600}>
                {title || `${t('orderDetails.linkedSpecimens')}`}
              </Text>
              <Badge size="xs" variant="light" mt={3}>
                {specimens.length}
              </Badge>
            </Group>
            {showAdd && (
              <Button
                variant="white"
                onClick={open}
                leftSection={<IconPlus size={14} />}
              >
                {t('buttons.add', { ns: 'common' })}
              </Button>
            )}
          </Group>

          {specimens.length === 0 ? (
            <Text size="sm" c="dimmed">
              {t('specimenSelection.noSpecimens', { ns: 'lims' })}
            </Text>
          ) : (
            <Stack gap="sm">
              {specimens.map((specimen) => (
                <SpecimenDisplayCard
                  key={specimen.id}
                  specimen={specimen}
                  onView={
                    onViewSpecimen ? () => onViewSpecimen(specimen) : undefined
                  }
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Specimen Selection Modal */}
      {orderFormData.sampleType && (
        <Drawer
          opened={showSpecimenModal}
          onClose={close}
          title={
            <Group gap="xs">
              <IconMicroscope size={18} />
              <Text fw={600}>
                {t('orderDetails.addSpecimensTitle', { ns: 'lims' })}
              </Text>
            </Group>
          }
          size="xl"
          position="right"
          offset={8}
          radius="md"
          closeOnEscape={false}
          closeOnClickOutside={false}
        >
          <SpecimenSelectionContent
            opened={showSpecimenModal}
            onClose={close}
            patient={patient}
            requiredSampleType={orderFormData.sampleType}
            serviceRequest={serviceRequest}
            linkedSpecimens={specimens}
            onSuccess={onAdd}
          />
        </Drawer>
      )}
    </>
  );
}
